import { normalizeVaultTenantId } from './vault-tenant-scope.util';
import { isSimappeProductCompanyLabel } from './vault-nebula-company.util';

const VAULT_TENANT_CATALOG_KEY = 'vault_tenant_catalog';
const VAULT_TENANT_CATALOG_LS_KEY = 'vault_tenant_catalog_ls';

export interface VaultTenantCatalogEntry {
  id: string;
  name: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Normaliza respuesta de `GET /api/admin/tenants`. */
export function normalizeVaultTenantCatalog(raw: unknown): VaultTenantCatalogEntry[] {
  let list: unknown[] = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else {
    const root = asRecord(raw);
    if (root) {
      for (const key of ['items', 'data', 'content', 'results', 'tenants'] as const) {
        const value = root[key];
        if (Array.isArray(value)) {
          list = value;
          break;
        }
      }
    }
  }

  const byId = new Map<string, VaultTenantCatalogEntry>();
  for (const entry of list) {
    const row = asRecord(entry);
    if (!row) continue;
    const id = String(row['id'] ?? row['tenant_id'] ?? row['tenantId'] ?? '').trim();
    const name = String(row['name'] ?? row['tenant_name'] ?? row['tenantName'] ?? '').trim();
    if (!id) continue;
    byId.set(id, { id, name: name || byId.get(id)?.name || '' });
  }
  return [...byId.values()];
}

export function persistVaultTenantCatalog(entries: VaultTenantCatalogEntry[]): void {
  const rows = (entries ?? [])
    .map((e) => ({ id: (e.id || '').trim(), name: (e.name || '').trim() }))
    .filter((e) => e.id);
  if (!rows.length) return;

  const payload = JSON.stringify(rows);
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(VAULT_TENANT_CATALOG_KEY, payload);
  }
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(VAULT_TENANT_CATALOG_LS_KEY, payload);
  }
}

function parseVaultTenantCatalogPayload(raw: string | null): VaultTenantCatalogEntry[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => {
        const row = asRecord(entry);
        if (!row) return null;
        const id = String(row['id'] ?? '').trim();
        if (!id) return null;
        return { id, name: String(row['name'] ?? '').trim() };
      })
      .filter((e): e is VaultTenantCatalogEntry => e != null);
  } catch {
    return [];
  }
}

export function readVaultTenantCatalog(): VaultTenantCatalogEntry[] {
  if (typeof sessionStorage !== 'undefined') {
    const fromSession = parseVaultTenantCatalogPayload(
      sessionStorage.getItem(VAULT_TENANT_CATALOG_KEY),
    );
    if (fromSession.length) return fromSession;
  }
  if (typeof localStorage !== 'undefined') {
    return parseVaultTenantCatalogPayload(localStorage.getItem(VAULT_TENANT_CATALOG_LS_KEY));
  }
  return [];
}

export function resolveVaultTenantNameFromCatalog(tenantId: string): string {
  const id = (tenantId || '').trim();
  if (!id) return '';
  const match = readVaultTenantCatalog().find((e) => e.id === id);
  const name = (match?.name ?? '').trim();
  if (!name || isSimappeProductCompanyLabel(name)) return '';
  return name;
}

/** Resuelve UUID de tenant por nombre (catálogo en sesión / snapshot). */
export function resolveVaultTenantIdByName(tenantName: string): string {
  const wanted = (tenantName || '').trim().toLowerCase();
  if (!wanted || isSimappeProductCompanyLabel(tenantName)) return '';
  for (const entry of readVaultTenantCatalog()) {
    const name = (entry.name || '').trim();
    if (!name || isSimappeProductCompanyLabel(name)) continue;
    if (name.toLowerCase() === wanted) {
      return normalizeVaultTenantId(entry.id) || entry.id;
    }
  }
  return '';
}

/** Guarda nombres desde `GET /api/tenants/my-tenants` (usuarios sin acceso a `/api/admin/tenants`). */
export function persistVaultTenantCatalogFromTenantRows(rows: unknown): void {
  if (!Array.isArray(rows) || !rows.length) return;
  const entries: VaultTenantCatalogEntry[] = [];
  for (const entry of rows) {
    const row = asRecord(entry);
    if (!row) continue;
    const explicit = String(row['tenant_id'] ?? row['tenantId'] ?? '').trim();
    const id = normalizeVaultTenantId(explicit) || explicit;
    if (!id) continue;
    const name = String(row['tenant_name'] ?? row['tenantName'] ?? row['name'] ?? '').trim();
    if (!name || isSimappeProductCompanyLabel(name)) continue;
    entries.push({ id, name });
  }
  if (entries.length) persistVaultTenantCatalog(entries);
}

export interface ModulePermissionTenantHint {
  user_id: string;
  tenant_id: string;
  tenant_name: string;
}

function unwrapModulePermissionsArray(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  const root = asRecord(raw);
  if (!root) return [];
  for (const key of ['items', 'data', 'content', 'results', 'permissions'] as const) {
    const value = root[key];
    if (Array.isArray(value)) return value;
  }
  return [];
}

/** Extrae empresa Vault desde `GET /api/module-permissions` o `/user/{id}`. */
export function extractModulePermissionTenantHints(raw: unknown): ModulePermissionTenantHint[] {
  const out: ModulePermissionTenantHint[] = [];
  const seen = new Set<string>();
  for (const entry of unwrapModulePermissionsArray(raw)) {
    const row = asRecord(entry);
    if (!row) continue;
    const user_id = String(row['user_id'] ?? row['userId'] ?? '').trim();
    const tenant_id = String(row['tenant_id'] ?? row['tenantId'] ?? '').trim();
    if (!user_id || !tenant_id) continue;
    const key = `${user_id}:${tenant_id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      user_id,
      tenant_id,
      tenant_name: String(row['tenant_name'] ?? row['tenantName'] ?? '').trim(),
    });
  }
  return out;
}

export function firstModulePermissionTenantHint(
  raw: unknown,
): ModulePermissionTenantHint | null {
  return extractModulePermissionTenantHints(raw)[0] ?? null;
}
